import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { ICompanySummary, ICompanySummaryData, ICompanySummaryWithDetails, IDatabaseDetail } from '../models/portal.model';
import { getCompanyDatabasesByUid } from "../api/portal.api";
import { handleThunkError } from '../common/util';
import { getDmsInfo, getDmsVersion } from '../api/dms.api';
import { IDmsInfo, IDmsInfoBlock } from '../models/dms.model';

interface CompanyState {
  companies: ICompanySummaryWithDetails[];
}

// Define the initial state using that type
const initialState: CompanyState = {
  companies: [],
};

const loadStateFromLocalStorage = () => {
  const companyDataString = localStorage.getItem('company');
  if (companyDataString) {
    const companyData = {
      companies: JSON.parse(companyDataString)
    } as CompanyState;
    return companyData;
  }

  return initialState;
};

/// THUNKS

export const fetchCompanyDataAsync = createAsyncThunk<{}, ICompanySummary>(
  'company/fetchCompanyDataAsync',
  async (company, { rejectWithValue, dispatch }) => {
    console.log('[company] fetchCompanyDataAsync');
    try {
      let hasError = false;
      let hasWarning = false;
      let databases = [] as IDatabaseDetail[];
      let version = 'UNKNOWN';
      let info = {} as IDmsInfo;

      try {
        databases = await getCompanyDatabasesByUid(company.uid);
        try {
          info = await getDmsInfo(databases[0]?.dbId, company.uid, true);
          // Check for warnings
          for (const [key, value] of Object.entries(info)) {
            const valProp = value as IDmsInfoBlock;
            hasWarning = valProp.hasError;
          }
        } catch (ex) {
          // If info fails, try to get version the old way
          version = (await getDmsVersion(databases[0]?.dbId, company.uid)).data;
          info.version = {
            result: version
          } as IDmsInfoBlock;
          throw ex;
        }
      } catch (ex) {
        hasError = true;
      }

      dispatch(setCompanyData({
        company: company,
        data: {
          databases,
          hasError,
          hasWarning,
          info,
        }
      }));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const removeCompanyAsync = createAsyncThunk<{}, string>(
  'company/removeCompanyAsync',
  async (companyUid, { rejectWithValue, dispatch, getState }) => {
    console.log('[company] removeCompanyAsync');
    const companies = [...(getState() as RootState).company.companies];
    const companyResults = companies.filter(c => c.uid !== companyUid);
    dispatch(setCompanies(companyResults));
  }
);

/// SLICE

export const companySlice = createSlice({
  name: 'company',
  initialState: loadStateFromLocalStorage(),
  reducers: {
    setCompanies: (state, action: PayloadAction<ICompanySummaryWithDetails[]>) => {
      console.log('[Company] setCompanies');
      state.companies = action.payload;
      localStorage.setItem('company', JSON.stringify(action.payload));
    },
    setCompanyData: (state, action: PayloadAction<{ company: ICompanySummary, data: ICompanySummaryData; }>) => {
      let allCompanies = [...state.companies];

      const matchingCompanyIndex = allCompanies.findIndex(company => company.uid === action.payload.company.uid);
      if (matchingCompanyIndex >= 0) {
        allCompanies.splice(matchingCompanyIndex, 1, {
          ...state.companies[matchingCompanyIndex],
          ...action.payload.data,
          hasData: true
        } as ICompanySummaryWithDetails);

        allCompanies.sort((x, y) => {
          if (x.hasError && !y.hasError) return -1;
          if (!x.hasError && y.hasError) return 1;

          if (x.name1 < y.name1) return -1;
          if (x.name1 > y.name1) return 1;

          return 0;
        });

        localStorage.setItem('company', JSON.stringify(allCompanies));
        state.companies = allCompanies;
      }
    },
  }
});

/// REDUCERS

export const { setCompanies, setCompanyData } = companySlice.actions;

/// SELECTORS

export const selectCompanies = (state: RootState) => state.company.companies;

export default companySlice.reducer;
